import { defineStore } from 'pinia';
import { COMPUTED_CALCULATORS, runOneCalculator, applyComputedProposals } from '@vft/core/computed';
import { programsForFields, setRemoteFieldPrograms, buildFieldProgramsFromRemote } from '@vft/config/processors/programBindings.js';
import { fetchProgramsByAliases } from '@vft/api/programService.js';
import { getCalculationStandalonePrograms, getRegistryVersion } from '@vft/config/standalonePrograms.js';
import { ensureModelDepsLoaded } from '@vft/api/standaloneProgramsApi.js';
import { instantiateRemoteProgram, importRemoteModule } from '@vft/core/computed/remoteLoader.js';
import { buildProgramContext, loadDeps, availableDeps } from '@vft/core/computed/programContext.js';
import { getRuntimeConfig, effectiveBaseUrl } from '@vft/config/runtimeConfig.js';
// El component és GENÈRIC: cap dataset ni lògica de client. Els programes del model
// (codi + dependències des de BD) fan tota la feina específica i emeten el seu propi
// `report` (que aquí es recull a reports[calcId] i es pinta de forma genèrica). Tot
// el reporting específic viu al `code` del programa (amb les seves deps), no aquí.
import { buildOutputRows, validateOutputRows } from '@vft/core/export/exporter.js';
import { useFileStore } from './fileStore.js';
import { useDefinitionStore } from './definitionStore.js';
import { useMappingStore } from './mappingStore.js';
import { useValueMapStore } from './valueMapStore.js';
import { useCorrectionsStore } from './correctionsStore.js';

// NO es persisteix res a localStorage: l'estat dels programes (activat/desactivat +
// config de slots) arranca SEMPRE per defecte cada sessió → tots els programes
// marcats (enabled = {}) i config per autodetecció. Així no es "recorda" un toggle
// apagat d'una sessió anterior.

// GENÈRIC: per a CADA camp amb propostes acceptades, afegeix una columna
// `<fieldId>__original` amb el valor previ a la correcció (de la pròpia proposta).
// No sap de cap camp concret: serveix per a qualsevol programa/camp.
function appendOriginalColumns(result, proposals, rejected) {
  const byField = new Map(); // fieldId → Map(rowIndex → originalValue)
  for (const p of proposals) {
    if (rejected[`${p.rowIndex}:${p.calculator}:${p.fieldId}`]) continue;
    let m = byField.get(p.fieldId);
    if (!m) { m = new Map(); byField.set(p.fieldId, m); }
    if (!m.has(p.rowIndex)) m.set(p.rowIndex, p.originalValue ?? '');
  }
  if (byField.size === 0) return result;
  const fieldIds = [...byField.keys()];
  const extraHeaders = fieldIds.map((f) => `${f}__original`);
  return {
    headers: [...result.headers, ...extraHeaders],
    rows: result.rows.map((row, i) => [
      ...row,
      ...fieldIds.map((f) => { const v = byField.get(f).get(i); return v == null ? '' : v; }),
    ]),
  };
}

export const useComputedStore = defineStore('computed', {
  state: () => ({
    proposals: [],
    reports: {},
    rejected: {},
    input: { headers: [], rows: [] },
    computed: false,
    running: false,
    enabled: {},
    config: {},
    lastConfigSnapshot: null,
    // Programes carregats des de Sherpa (task aliasprogram). Buit ⇒ fallback al
    // binding estàtic de programBindings.js.
    remotePrograms: [],
    programsLoaded: false,
    // Versió del registre de programes de fitxer amb què es va carregar (per recarregar
    // si s'edita un programa/dep a l'ABM sense recàrrega de pàgina).
    programsRegistryVersion: -1,
    // Firma (alias ordenats) del formulari amb què es van carregar els programes.
    // Si el formulari canvia (altres camps → altres programes), la precàrrega
    // idempotent ha de tornar a carregar; si no, la llista de programes quedaria
    // obsoleta (p.ex. carregar un form de vehicle i després un d'email).
    programsFieldsKey: null,
    // Calculadores instanciades des del `code` del model (id → calculadora). El
    // motor les fa servir en lloc de les estàtiques quan el `code` carrega net.
    remoteCalculators: {},
    // Metadades de cada programa del model (id → { type, order }), des de BD. El
    // motor les fa servir per separar PREPROCESS (transformen files, s'executen
    // primer per `order`) de COMPUTE (proposen canvis per camp).
    remoteCalculatorMeta: {},
    // Errors d'execució del `code` del model per programa (name → missatge).
    // De moment els programes porten imports interns sense resoldre → s'omple
    // i es pinta. Buit quan el `code` carregui net (fase d'imports resolta).
    programErrors: {},
    // CONSOLA EN VIU del pas 7: línies del que va fent el motor (carregar
    // programes, instanciar amb deps, executar per lots, resultats, errors). La
    // UI la pinta tipus terminal amb auto-scroll. Cada línia: { seq, level, text, ts }.
    executionLog: [],
    logSeq: 0,
    // Progrés del programa en execució (per a la barra): { calcId, name, processed, total }
    // o null quan no s'està executant cap lot.
    progress: null,
    // Estat EN VIU per programa per al timeline (ProgramPipeline): id → { status:
    // pending|running|done|error|skipped, proposals, durationMs, error, processed, total }.
    // S'omple a compute() reaprofitant els valors que el bucle ja calcula; NO canvia
    // la lògica d'execució ni el resultat.
    programRuntime: {},
  }),
  getters: {
    effectiveConfig() {
      const def = useDefinitionStore();
      const fields = def.fields;
      const fieldIds = new Set(fields.map((f) => f.id || f.name || f.label));
      const out = {};
      // Iterem els programes del model (remots) si n'hi ha; si no, els estàtics.
      const remote = Object.values(this.remoteCalculators);
      const calcs = remote.length ? remote : COMPUTED_CALCULATORS;
      for (const calc of calcs) {
        if (!calc.configurable) continue;
        const auto = typeof calc.getAutoConfig === 'function' ? calc.getAutoConfig(fields) : {};
        const saved = this.config[calc.id];
        if (!saved) {
          out[calc.id] = auto;
          continue;
        }
        // Sanititza la config desada contra els camps del formulari actual: si un
        // slot apunta a un camp que ja no existeix (config d'un altre formulari),
        // cau a l'autodetecció. Així una config antiga no amaga el programa.
        const slots = calc.configSlots || Object.keys(saved).map((key) => ({ key }));
        const merged = {};
        for (const { key } of slots) {
          if (Object.prototype.hasOwnProperty.call(saved, key)) {
            const v = saved[key];
            merged[key] = v && !fieldIds.has(v) ? (auto[key] ?? null) : v;
          } else {
            merged[key] = auto[key] ?? null;
          }
        }
        out[calc.id] = merged;
      }
      return out;
    },
    applicableCalculators() {
      // FONT: els programes del model (carregats de BD via loadPrograms). El task
      // ja els torna filtrats pels àlies del formulari → són directament els
      // aplicables. Els estàtics només són fallback (backend no disponible o abans
      // de la primera càrrega). Ordenats com s'executen: PREPROCESS primer (per
      // `order`), després els de càlcul.
      const ids = Object.keys(this.remoteCalculators);
      if (ids.length) {
        const rank = (id) => (this.remoteCalculatorMeta[id]?.type === 'preprocessing' ? 0 : 1);
        const ord = (id) => this.remoteCalculatorMeta[id]?.order || 0;
        return ids
          .sort((a, b) => (rank(a) - rank(b)) || (ord(a) - ord(b)))
          .map((id) => this.remoteCalculators[id]);
      }
      const def = useDefinitionStore();
      const triggered = new Set(programsForFields(def.fields));
      return COMPUTED_CALCULATORS.filter((c) => triggered.has(c.id));
    },
    configDirty() {
      if (!this.computed) return false;
      return JSON.stringify(this.effectiveConfig) !== this.lastConfigSnapshot;
    },
    acceptedProposals: (s) => s.proposals.filter((p) => !s.rejected[`${p.rowIndex}:${p.calculator}:${p.fieldId}`]),
    rejectedProposals: (s) => s.proposals.filter((p) => s.rejected[`${p.rowIndex}:${p.calculator}:${p.fieldId}`]),
    computedRows() {
      if (!this.computed) return this.input;
      const result = applyComputedProposals(this.input, this.proposals, this.rejected);
      return appendOriginalColumns(result, this.proposals, this.rejected);
    },
    computedReport() {
      const def = useDefinitionStore();
      if (!def.hasDefinition) return { totalRows: 0, rowsWithErrors: 0, totalErrors: 0, errors: [] };
      return validateOutputRows(this.computedRows, def.fields);
    },
    summary() {
      const accepted = this.acceptedProposals.length;
      const rejected = this.rejectedProposals.length;
      const byCalc = {};
      const byField = {};
      for (const p of this.acceptedProposals) {
        byCalc[p.calculator] = (byCalc[p.calculator] || 0) + 1;
        byField[p.fieldId] = (byField[p.fieldId] || 0) + 1;
      }
      return { total: this.proposals.length, accepted, rejected, byCalc, byField };
    },
    // Reports GENÈRICS emesos pels programes: per programa, reports[calcId].report.sections[]
    // (cada secció = { title, summary?, columns:[{key,label,align?}], rows:[…] }). Aplanat
    // amb el nom visible del programa (description de BD) per pintar-lo sense saber de
    // cap client. La FONT del contingut és el `code` del programa (model), no el component.
    programReports() {
      const out = [];
      for (const [calcId, rep] of Object.entries(this.reports)) {
        const sections = (rep && rep.report && Array.isArray(rep.report.sections)) ? rep.report.sections : [];
        if (!sections.length) continue;
        const meta = this.remoteCalculatorMeta[calcId] || {};
        out.push({ calcId, programName: meta.description || meta.name || calcId, sections });
      }
      return out;
    },
    // Resum GENÈRIC de canvis: agrupa les propostes acceptades per (camp, original→proposat)
    // i compta. No sap de cap client ni de cap camp concret.
    changesSummary() {
      const groups = new Map();
      for (const p of this.acceptedProposals) {
        const orig = p.originalValue == null ? '' : String(p.originalValue);
        const prop = p.proposedValue == null ? '' : String(p.proposedValue);
        // La clau inclou el programa (calculator): el resum atribueix cada tipus de
        // canvi al programa que l'ha proposat (columna «Programa» de la taula).
        const key = `${p.calculator}|||${p.fieldId}|||${orig}|||${prop}`;
        let g = groups.get(key);
        if (!g) { g = { calculator: p.calculator, fieldId: p.fieldId, original: orig, proposed: prop, count: 0 }; groups.set(key, g); }
        g.count++;
      }
      return [...groups.values()].sort((a, b) => b.count - a.count);
    },
    // Ordre d'execució dels programes del model: PREPROCESS primer (per `order`),
    // després els de càlcul. FONT ÚNICA d'ordre, usada tant pel timeline (pipeline)
    // com per compute() → la UI no pot divergir de l'execució real.
    orderedProgramIds() {
      const ids = Object.keys(this.remoteCalculators);
      const rank = (id) => (this.remoteCalculatorMeta[id]?.type === 'preprocessing' ? 0 : 1);
      const ord = (id) => this.remoteCalculatorMeta[id]?.order || 0;
      return ids.sort((a, b) => (rank(a) - rank(b)) || (ord(a) - ord(b)));
    },
    // Passos del pipeline per al component ProgramPipeline (pas 7). Combina l'ordre
    // d'execució + metadades del model + l'estat en viu de programRuntime.
    pipeline() {
      return this.orderedProgramIds.map((id) => {
        const meta = this.remoteCalculatorMeta[id] || {};
        const rt = this.programRuntime[id] || {};
        return {
          id,
          label: meta.description || meta.name || id,
          name: meta.name || '',
          type: meta.type || 'compute',
          origin: meta.origin || 'catalog',
          status: rt.status || 'pending',
          proposals: rt.proposals,
          durationMs: rt.durationMs,
          error: rt.error,
          processed: rt.processed,
          total: rt.total,
        };
      });
    },
  },
  actions: {
    // Patch reactiu de l'estat en viu d'un programa per al timeline.
    setRuntime(id, patch) {
      this.programRuntime = { ...this.programRuntime, [id]: { ...(this.programRuntime[id] || {}), ...patch } };
    },
    // Buida la consola d'execució (es crida a l'inici de cada compute()).
    clearLog() {
      this.executionLog = [];
      this.logSeq = 0;
      this.progress = null;
      this.programRuntime = {};
    },
    // Afegeix una línia a la consola en viu. level ∈ info|load|run|ok|warn|error|batch.
    pushLog(level, text) {
      this.executionLog.push({ seq: this.logSeq++, level, text });
      // Sostre de seguretat: no deixem créixer el log sense límit amb fitxers grans.
      if (this.executionLog.length > 500) this.executionLog.splice(0, this.executionLog.length - 500);
    },
    // Carrega els programes de transformació des de Sherpa (task aliasprogram) i
    // injecta el binding viu àlies→programa. La FONT passa a ser el model; el
    // binding estàtic de programBindings.js queda com a fallback si el backend no
    // respon (standalone sense sessió). `log` (opcional) escriu a la consola en viu;
    // per defecte no-op (precàrrega silenciosa abans de Recalcular).
    async loadPrograms(log = () => {}) {
      const def = useDefinitionStore();
      const fieldIds = (def.fields || [])
        .map((f) => f.id || f.name || f.label)
        .filter(Boolean);
      this.programsFieldsKey = fieldIds.slice().sort().join('|');
      this.programsRegistryVersion = getRegistryVersion();
      // Fetch dels programes del CATÀLEG aïllat: si el backend no respon (standalone
      // sense sessió) degradem a llista buida però SEGUIM amb els de component.
      let fetched = [];
      try {
        log('load', 'Carregant programes del model (task aliasprogram)…');
        // Els programes 'correction' viuen al pas 6 (Correcció), encara que tinguin
        // àlies assignats (scope BY_ALIAS). El pas 7 (Camps calculats) només mostra
        // preprocessing/compute → s'exclouen les correccions del resultat per àlies.
        fetched = (await fetchProgramsByAliases(fieldIds))
          .filter((p) => !/^correction$/i.test(p.type || ''));
        this.programsLoaded = true;
      } catch (e) {
        this.programsLoaded = false;
        log('warn', `Backend no disponible (${e && e.message ? e.message : e}) — només programes de component.`);
      }
      try {
        // Si hi ha programes de fitxer, assegura que les dependències del MODEL estan
        // carregades (perquè un programa en local pugui referenciar-ne). Idempotent.
        if (getCalculationStandalonePrograms().length) await ensureModelDepsLoaded();
        // Programes de CÀLCUL de FITXER (standalonePrograms/calculation) — només
        // standalone. Gate de coincidència d'àlies (M046, com el backend amb els del
        // catàleg): sense àlies declarats → sempre; amb matchAll → TOTS els àlies han
        // de ser camps del formulari; per defecte (ALGUN) n'hi ha prou amb un.
        const fileCalc = getCalculationStandalonePrograms().filter(
          (p) =>
            !(p.aliases && p.aliases.length) ||
            (p.matchAll
              ? p.aliases.every((a) => fieldIds.includes(a))
              : p.aliases.some((a) => fieldIds.includes(a))),
        );
        const programs = [...fetched, ...fileCalc];
        this.remotePrograms = programs;
        this.programsLoaded = true;
        setRemoteFieldPrograms(buildFieldProgramsFromRemote(programs));
        log('ok', `${programs.length} programa(es) rebut(s) del catàleg: ${programs.map((p) => p.name || p.id).join(', ') || '∅'}`);

        // Intentem INSTANCIAR el `code` de cada programa del model amb el
        // contracte create(ctx) (veure programContext.js). ctx.deps es construeix
        // des de la BD — cada programa declara les seves dependències (taula
        // transform_program_dependency) i el task les torna amb el seu contingut.
        // Els JSON es parsegen a objecte; els JS es carreguen com a mòdul (Blob+import).
        // Si l'avaluació del `code` llança, es captura i es pinta a la consola.
        // Si el backend no en torna cap dep (versió antiga), fallback als datasets locals.
        const deps = {};
        for (const p of programs) {
          for (const d of (p.dependencies || [])) {
            if (!d || !d.name || d.name in deps) continue;
            try {
              if (d.type === 'js') {
                // dep JS → es carrega dinàmicament com a mòdul (Blob + import),
                // igual que el `code` d'un programa. El programa rep l'export.
                deps[d.name] = await importRemoteModule(d.content);
              } else if (d.type === 'json' && typeof d.content === 'string') {
                deps[d.name] = JSON.parse(d.content);
              } else {
                deps[d.name] = d.content;
              }
              const sz = typeof d.content === 'string' ? ` (${(d.content.length / 1024).toFixed(0)} KB)` : '';
              log('load', `↳ dependència «${d.name}» [${d.type}]${sz} carregada`);
            } catch (depErr) {
              deps[d.name] = d.content; // no carregable → cru; el programa decidirà
              log('warn', `↳ dependència «${d.name}» [${d.type}] no carregable: ${depErr && depErr.message ? depErr.message : depErr}`);
            }
          }
        }
        if (Object.keys(deps).length === 0) {
          try {
            Object.assign(deps, await loadDeps(availableDeps()));
          } catch (depErr) {
            /* sense deps */
          }
        }
        const rc = getRuntimeConfig();
        const ctx = buildProgramContext({
          fields: def.fields || [],
          config: this.config, // raw (effectiveConfig depèn de remoteCalculators → circular aquí)
          deps,
          sherpa: { baseUrl: effectiveBaseUrl(rc), token: rc.token },
        });
        const errors = {};
        const calculators = {};
        const meta = {};
        for (const p of programs) {
          const key = p.name || p.id;
          try {
            log('load', `Instanciant «${key}» amb create(ctx)…`);
            const calc = await instantiateRemoteProgram(p, ctx);
            if (calc) {
              // El `calc.id` viene hardcodeado dentro del `code` del programa (p.ej.
              // 'component:geoEnrich') → si el mismo programa vive en el catálogo del
              // model Y como fichero standalone, ambos retornan el mismo calc.id y uno
              // pisaría al otro en `remoteCalculators`. Forzamos `calc.id = p.id`
              // (único por origen: FTP-… del model vs component:… del standalone) para
              // que ambos convivan y sean seleccionables por separado en el paso 7.
              if (p.id) calc.id = p.id;
              if (!calc.id) continue;
              calculators[calc.id] = calc;
              // type/order/name/description del MODEL (BD); `order` ve com a string
              // del task. name/description són el text editable des de la secció
              // «Programes» → la UI pinta el `description` de BD (no l'i18n del
              // component), perquè el nom del programa també viu al model.
              const type = p.type || calc.type || 'compute';
              meta[calc.id] = {
                type,
                order: Number(p.order != null ? p.order : (calc.order != null ? calc.order : 0)) || 0,
                name: p.name || '',
                description: p.description || '',
                origin: p.origin || 'catalog',
              };
              const depNames = (p.dependencies || []).map((d) => d.name).filter(Boolean);
              log('ok', `✓ «${key}» llest [${type}]${depNames.length ? ' · deps: ' + depNames.join(', ') : ''}`);
            }
          } catch (err) {
            // Guardem l'error TAL CUAL (stack si n'hi ha) per pintar-lo com a consola.
            errors[key] = (err && err.stack)
              ? String(err.stack)
              : (err && err.message ? `${err.name || 'Error'}: ${err.message}` : String(err));
            log('error', `✗ ERROR instanciant «${key}»: ${err && err.message ? err.message : err}`);
            break; // en cuant hi ha un error, l'agafem i parem
          }
        }
        this.programErrors = errors;
        // Si algun programa ha petat, compute() avorta igualment; només fem servir
        // les calculadores del model quan TOTES han carregat net.
        this.remoteCalculators = Object.keys(errors).length ? {} : calculators;
        this.remoteCalculatorMeta = Object.keys(errors).length ? {} : meta;
      } catch (e) {
        // Backend no disponible (standalone, sense sessió…) → binding estàtic.
        this.remotePrograms = [];
        this.programsLoaded = false;
        this.programErrors = {};
        this.remoteCalculators = {};
        setRemoteFieldPrograms(null);
        log('warn', `Backend no disponible (${e && e.message ? e.message : e}) — sense programes del model.`);
      }
    },

    // Càrrega ANTICIPADA dels programes del model (per pintar la llista/config a la
    // UI abans de Recalcular). Idempotent: només carrega un cop per sessió de dades.
    async ensureProgramsLoaded() {
      const def = useDefinitionStore();
      if (!def.hasDefinition) return;
      // Recarrega si encara no s'han carregat O si el formulari ha canviat (els
      // seus alias decideixen quins programes aplica el task) → llista sempre vigent.
      const key = (def.fields || []).map((f) => f.id || f.name || f.label).filter(Boolean).slice().sort().join('|');
      if (this.programsLoaded && this.programsFieldsKey === key && this.programsRegistryVersion === getRegistryVersion()) return;
      await this.loadPrograms();
    },

    async compute() {
      const def = useDefinitionStore();
      const file = useFileStore();
      const mapping = useMappingStore();
      const valueMap = useValueMapStore();
      const corrections = useCorrectionsStore();
      if (!def.hasDefinition || !file.hasData) {
        this.proposals = [];
        this.input = { headers: [], rows: [] };
        this.computed = false;
        return;
      }
      this.running = true;
      this.clearLog();
      const log = (level, text) => this.pushLog(level, text);
      const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : 0);
      const t0 = now();
      // Nom visible d'un programa (description de BD, fallback name/id).
      const nameOf = (id) => {
        const m = this.remoteCalculatorMeta[id] || {};
        return m.description || m.name || id;
      };
      // onEvent comú del runner: alimenta la barra de progrés (i avisa d'execució atòmica).
      const makeOnEvent = (id, name) => (evt) => {
        if (evt.type === 'batch') {
          this.progress = { calcId: id, name, processed: evt.processed, total: evt.total };
          this.setRuntime(id, { processed: evt.processed, total: evt.total });
        } else if (evt.type === 'atomic') {
          this.progress = { calcId: id, name, processed: 0, total: evt.total };
          this.setRuntime(id, { processed: 0, total: evt.total });
          log('warn', `«${name}»: report agregat → execució atòmica (sense progrés per lots)`);
        }
      };
      try {
        log('info', '▶ Inici del càlcul');
        // Binding de programes des del model (task Sherpa) abans d'executar.
        await this.loadPrograms(log);
        // Si el `code` d'un programa falla en instanciar-se, ho mostrem i ATUREM.
        if (Object.keys(this.programErrors).length > 0) {
          log('error', 'Càlcul aturat: hi ha programes amb errors d\'instanciació.');
          this.proposals = [];
          this.reports = {};
          this.input = { headers: [], rows: [] };
          this.computed = false;
          return;
        }
        const fallback = buildOutputRows(
          { headers: file.headers, rows: file.rows },
          def.fields,
          mapping.mapping,
          {
            autoValues: mapping.autoValues,
            columnActions: mapping.columnActions,
            extraTargets: mapping.extraTargets,
            valueMap: valueMap.byField,
          },
        );
        const corrected = corrections.computed && corrections.acceptedProposals.length > 0
          ? corrections.correctedRows
          : fallback;
        log('info', `Dades d'entrada: ${corrected.rows.length.toLocaleString()} files · ${corrected.headers.length} columnes`);

        // Executa UN programa amb feedback (progrés + consola). Torna el resultat
        // del runner o llança (l'embolcall de dalt el captura i el pinta).
        const runProgram = async (id, kind, inputRows) => {
          const calc = this.remoteCalculators[id];
          const name = nameOf(id);
          if (this.enabled[calc.id] === false) { log('info', `⏭ «${name}» desactivat`); this.setRuntime(id, { status: 'skipped' }); return null; }
          const cfg = this.effectiveConfig[calc.id];
          if (typeof calc.appliesTo === 'function' && !calc.appliesTo(def.fields, cfg)) {
            log('info', `⏭ «${name}» no aplica (camps absents)`);
            this.setRuntime(id, { status: 'skipped' });
            return null;
          }
          log('run', `⚙ ${kind} «${name}» sobre ${inputRows.rows.length.toLocaleString()} files…`);
          this.setRuntime(id, { status: 'running', processed: 0, total: inputRows.rows.length });
          const ti = now();
          let res;
          try {
            res = await runOneCalculator(calc, inputRows, def.fields, { config: cfg, onEvent: makeOnEvent(id, name) });
          } catch (err) {
            const stack = (err && err.stack) ? String(err.stack) : (err && err.message ? `${err.name || 'Error'}: ${err.message}` : String(err));
            log('error', `✗ ERROR executant «${name}»: ${err && err.message ? err.message : err}`);
            this.programErrors = { ...this.programErrors, [id]: stack };
            this.setRuntime(id, { status: 'error', error: stack });
            throw err;
          }
          this.progress = null;
          this.setRuntime(id, { status: 'done', proposals: res.proposals.length, durationMs: now() - ti });
          log('ok', `✓ «${name}»: ${res.proposals.length.toLocaleString()} proposta(es)${res.report ? ' · report emès' : ''} · ${(now() - ti).toFixed(0)} ms`);
          return res;
        };

        // Ordre d'execució (FONT ÚNICA = orderedProgramIds, també usat pel timeline):
        // PREPROCESS primer (per `order`) i després els de càlcul. Així el pipeline que
        // pinta la UI no pot divergir de l'ordre real en què s'executen.
        const ordered = this.orderedProgramIds;
        for (const id of ordered) this.setRuntime(id, { status: 'pending' });
        const isPre = (id) => this.remoteCalculatorMeta[id] && this.remoteCalculatorMeta[id].type === 'preprocessing';
        // PREPROCESS: transformen les FILES abans dels càlculs i poden proposar i emetre report.
        let pipelineInput = corrected;
        const proposals = [];
        const reports = {};
        const preprocs = ordered.filter(isPre);
        for (const id of preprocs) {
          const res = await runProgram(id, 'Preprocés', pipelineInput);
          if (!res) continue;
          if (res.transformedRows != null) pipelineInput = { headers: pipelineInput.headers, rows: res.transformedRows };
          if (res.proposals.length) proposals.push(...res.proposals);
          if (res.report) reports[id] = { report: res.report };
        }
        this.input = pipelineInput;

        // COMPUTE (type≠preprocessing): proposen canvis per camp sobre les dades ja
        // preprocessades.
        const computeIds = ordered.filter((id) => !isPre(id));
        for (const id of computeIds) {
          const res = await runProgram(id, 'Càlcul', this.input);
          if (!res) continue;
          if (res.proposals.length) proposals.push(...res.proposals);
          if (res.report) reports[id] = { report: res.report };
        }

        this.proposals = proposals;
        // Reports per programa (preprocess + compute): cada run()/buildReport pot emetre
        // report.sections; programReports els aplana per pintar-los de forma genèrica.
        this.reports = reports;
        this.computed = true;
        this.lastConfigSnapshot = JSON.stringify(this.effectiveConfig);
        log('ok', `▣ Fet: ${proposals.length.toLocaleString()} proposta(es) en total · ${(now() - t0).toFixed(0)} ms`);
      } catch (err) {
        // Error no controlat (o propagat des d'un programa) → es pinta i s'atura,
        // sense bloquejar el navegador (queda capturat aquí).
        if (Object.keys(this.programErrors).length === 0) {
          const stack = (err && err.stack) ? String(err.stack) : String(err);
          this.programErrors = { execucio: stack };
          log('error', `✗ ERROR d'execució: ${err && err.message ? err.message : err}`);
        }
        this.proposals = [];
        this.reports = {};
        this.computed = false;
      } finally {
        this.progress = null;
        this.running = false;
      }
    },
    setEnabled(calcId, enabled) {
      this.enabled = { ...this.enabled, [calcId]: enabled };
      if (this.computed) this.compute();
    },
    setConfig(calcId, partial) {
      const current = this.config[calcId] || {};
      this.config = { ...this.config, [calcId]: { ...current, ...partial } };
    },
    resetConfig(calcId) {
      const next = { ...this.config };
      delete next[calcId];
      this.config = next;
    },
    toggleProposal(rowIndex, calculator, fieldId) {
      const key = `${rowIndex}:${calculator}:${fieldId}`;
      this.rejected = { ...this.rejected, [key]: !this.rejected[key] };
    },
    // Accepta/rebutja TOTES les propostes d'una fila (mateix rowIndex+calculator) alhora.
    // Serveix a la revisió PER FILA (origen sencer → sortida sencera), no camp a camp.
    setGroupRejected(rowIndex, calculator, fieldIds, rejected) {
      const next = { ...this.rejected };
      for (const fieldId of fieldIds) {
        const key = `${rowIndex}:${calculator}:${fieldId}`;
        if (rejected) next[key] = true;
        else delete next[key];
      }
      this.rejected = next;
    },
    acceptAll(filter) {
      const next = { ...this.rejected };
      for (const p of this.proposals) {
        if (filter && !filter(p)) continue;
        delete next[`${p.rowIndex}:${p.calculator}:${p.fieldId}`];
      }
      this.rejected = next;
    },
    rejectAll(filter) {
      const next = { ...this.rejected };
      for (const p of this.proposals) {
        if (filter && !filter(p)) continue;
        next[`${p.rowIndex}:${p.calculator}:${p.fieldId}`] = true;
      }
      this.rejected = next;
    },
    reset() {
      this.proposals = [];
      this.reports = {};
      this.rejected = {};
      this.input = { headers: [], rows: [] };
      this.computed = false;
    },
  },
});
